import { ObjectProperty } from 'api/objectProperties/objectProperties.api'
import { Setting, useObjectPropertySettings } from 'api/settings/settings.api'
import { getSettingValue, getTextFromValue } from 'helpers/global.helper/global.helper'
import { isActiveSetting, isHistorySetting } from 'helpers/optimize.helper/optimize.helper'
import { formatClassificationString } from 'helpers/settingsModal.helper/settingsModal.helper'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import CustomTable, { LegacyColumn, LegacyTableOptions } from 'ui/components/CustomTable/CustomTable'
import TimeField from 'ui/components/CustomTable/components/TimeField/TimeField'
import { ActiveRow } from 'ui/components/ObjectPropertyFormSelector/components/ObjectPropertySettingForm/ObjectPropertySettingForm'
import { SettingsPermissions } from 'ui/components/SettingsTable/SettingsTable'
import CommentField from './components/CommentField/CommentField'
import ManageField from './components/ManageField/ManageField'
import styles from './PreviousSettingsTable.module.less'

const tableOptions: Partial<LegacyTableOptions<any>> = {
  initialState: {
    sortBy: [{ id: 'updatedAt', desc: true }],
    hiddenColumns: ['id'],
    pageSize: 5,
  },
}

type PreviousSettingsTableProps = {
  activeRow?: ActiveRow
  objectProperty: ObjectProperty
  isBinaryProperty: boolean
  unit: string
  isBaseValue?: boolean
  permissions: SettingsPermissions
  onEditClick: (
    comment: string | null,
    end_time: string | null,
    start_time: string,
    id: number,
    value: string,
    classification?: string
  ) => void
  onDeleteClick: (id: number) => void
}

export default function PreviousSettingsTable({
  activeRow,
  unit,
  objectProperty,
  isBinaryProperty,
  isBaseValue,
  onEditClick,
  onDeleteClick,
  permissions,
}: PreviousSettingsTableProps): ReactElement {
  const { t } = useTranslation()

  const { data: allSettings } = useObjectPropertySettings(objectProperty.id, { is_base_value: isBaseValue })

  const activeSettings = allSettings?.filter((setting) => isActiveSetting(setting)) ?? []

  const historySettings = allSettings?.filter((setting) => isHistorySetting(setting)) ?? []

  const generateColumns = useCallback(
    (edit?: boolean): LegacyColumn[] => [
      { Header: 'Id', accessor: 'id' },
      {
        Header: t('Start time'),
        accessor: 'startTime',
        Cell: ({ value }: { value: any }) => <TimeField value={value} />,
      },
      {
        Header: t('End time'),
        accessor: 'endTime',
        Cell: ({ value }: { value: any }) => <TimeField value={value} />,
      },
      { Header: t('Value '), accessor: 'value' },
      { Header: t('Unit '), accessor: 'unit' },

      { Header: t('Cause '), accessor: 'comment', Cell: ({ value }: { value: any }) => <CommentField value={value} /> },
      {
        Header: t('Last updated'),
        accessor: 'updatedAt',
        Cell: ({ value }: { value: any }) => <TimeField value={value} />,
      },
      ...(edit ? [{ Header: t('Manage'), accessor: 'manage', disableSortBy: true }] : []),
    ],
    [t]
  )

  const activeSettingsColumns = React.useMemo(() => generateColumns(true), [generateColumns])
  const historySettingsColumns = React.useMemo(() => generateColumns(true), [generateColumns])

  const generateRows = useCallback(
    (settings: Setting[], canEdit?: boolean): Record<string, string | number | ReactElement | null>[] => {
      return settings.map((setting) => {
        const mappedValue = getTextFromValue(setting.value, objectProperty.value_mappings)
        const settingValue = mappedValue
          ? mappedValue
          : isBinaryProperty
            ? getSettingValue(setting.value)
            : setting.value

        const classificationTranslation = formatClassificationString(setting.classification) ?? ' - '

        return {
          id: setting.id,
          startTime: setting.start_time,
          endTime: setting.end_time,
          value: settingValue,
          unit: unit,
          comment: setting.comment,
          classification: classificationTranslation,
          updatedAt: setting.updated_at,
          ...(canEdit
            ? {
                manage: (
                  <ManageField
                    key={setting.id}
                    onEditClick={() => {
                      onEditClick(
                        setting.comment,
                        setting.end_time,
                        setting.start_time,
                        setting.id,
                        String(setting.value),
                        setting.classification
                      )
                    }}
                    onDeleteClick={() => {
                      onDeleteClick(setting.id)
                    }}
                    isDisabled={!canEdit}
                    isEditing={
                      activeRow && activeRow.status === 'editing' && activeRow.id === setting.id ? true : false
                    }
                  />
                ),
              }
            : null),
        }
      })
    },
    [activeRow, isBinaryProperty, objectProperty.value_mappings, onDeleteClick, onEditClick, unit]
  )

  const activeSettingsData = React.useMemo(
    () => generateRows(activeSettings, permissions.canEdit),
    [activeSettings, generateRows, permissions.canEdit]
  )
  const historySettingsData = React.useMemo(
    () => generateRows(historySettings, permissions.canEditHistoric),
    [historySettings, generateRows, permissions.canEditHistoric]
  )

  return (
    <div className={styles.PreviousSettingsTable_Container}>
      <CustomTable
        columns={activeSettingsColumns}
        data={activeSettingsData}
        title={t('Active settings')}
        tableOptions={tableOptions}
        emptyTableText={t('No active settings found')}
        rowClass={styles.PreviousSettingsTable_Background}
      />
      <CustomTable
        columns={historySettingsColumns}
        data={historySettingsData}
        title={t('Previous settings')}
        tableOptions={tableOptions}
        emptyTableText={t('No historic settings found')}
        rowClass={styles.PreviousSettingsTable_Background}
      />
    </div>
  )
}
